import React from 'react';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import { AssignmentsMetrics } from '../models';

export interface MetricsBarProps {
  metrics: AssignmentsMetrics;
}

/**
 * Horizontal metrics bar displayed above the process-path sections on the
 * Assignments page. Pure presentational: renders two rows of aggregate
 * counts derived from the `metrics` prop.
 *
 * Row 1 ("Associates"): Unlocked, Clocked in, Actual — displayed as bold
 *         numbers with smaller category labels.
 * Row 2 ("Assigned"): Primary, Problem solve, Resale, Clean decant, Audit —
 *         same bold-number + label pattern.
 */
const MetricsBar: React.FC<MetricsBarProps> = ({ metrics }) => {
  const {
    totalUnlocked,
    totalClockedIn,
    totalActual,
    assignedPrimary,
    assignedProblemSolve,
    assignedResale,
    assignedCleanDecant,
    assignedIndirect,
  } = metrics;

  return (
    <Box className="assignments-metrics-bar" spacingInset="300" data-testid="metrics-bar">
      <div className="assignments-metrics-bar__row">
        <Text type="b200" color="secondary" className="assignments-metrics-bar__section-label">
          Associates
        </Text>
        <div className="assignments-metrics-bar__items">
          <div className="assignments-metrics-bar__item">
            <Text type="h100" tag="span">{totalUnlocked}</Text>
            <Text type="b100" color="secondary" tag="span">Unlocked</Text>
          </div>
          <div className="assignments-metrics-bar__item">
            <Text type="h100" tag="span">{totalClockedIn}</Text>
            <Text type="b100" color="secondary" tag="span">Clocked in</Text>
          </div>
          <div className="assignments-metrics-bar__item">
            <Text type="h100" tag="span">{totalActual}</Text>
            <Text type="b100" color="secondary" tag="span">Actual</Text>
          </div>
        </div>
      </div>
      <div className="assignments-metrics-bar__row">
        <Text type="b200" color="secondary" className="assignments-metrics-bar__section-label">
          Assigned
        </Text>
        <div className="assignments-metrics-bar__items">
          <div className="assignments-metrics-bar__item">
            <Text type="h100" tag="span">{assignedPrimary}</Text>
            <Text type="b100" color="secondary" tag="span">Primary</Text>
          </div>
          <div className="assignments-metrics-bar__item">
            <Text type="h100" tag="span">{assignedProblemSolve}</Text>
            <Text type="b100" color="secondary" tag="span">Problem solve</Text>
          </div>
          <div className="assignments-metrics-bar__item">
            <Text type="h100" tag="span">{assignedResale}</Text>
            <Text type="b100" color="secondary" tag="span">Resale</Text>
          </div>
          <div className="assignments-metrics-bar__item">
            <Text type="h100" tag="span">{assignedCleanDecant}</Text>
            <Text type="b100" color="secondary" tag="span">Clean decant</Text>
          </div>
          <div className="assignments-metrics-bar__item">
            <Text type="h100" tag="span">{assignedIndirect}</Text>
            <Text type="b100" color="secondary" tag="span">Audit</Text>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default MetricsBar;
